import { osnovaApi } from '../base';
import type {
  KadryBotChannel,
  KadryBotStatus,
  KadryBudget,
  KadryConnectStart,
  KadryInterestedRequest,
  KadryNotification,
  KadryOutreach,
  KadryOutreachComment,
  KadryOverview,
  KadryProcessingProfile,
  KadryProcessingProfileInput,
  KadryRegistry,
  KadrySearchCost,
  KadrySearchRun,
  KadrySecretKind,
  KadrySecretMeta,
  KadrySmsStatus,
  KadrySourceAccount,
  KadryTariff,
  KadryVacancyLink,
} from '@entities/kadry';

const BASE = '/agents/kadry';

const ifMatch = (version: number) => ({ 'If-Match': `"${version}"` });

/**
 * ОСНОВА.Кадры — поиск на площадках, реестр кандидатов, предложения
 * кандидатам и подключения (сценарий заказчика Р10–Р13, ADR-0019).
 *
 * Реестр заявки обновляется сам: WS-событие `kadry_notification` (ответ
 * кандидата, новые кандидаты) инвалидирует теги `KadryRegistry` и
 * `KadryNotification`, а экран реестра дополнительно опрашивает сервер,
 * пока идёт поиск.
 */
export const kadryOutreachApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    // ===== Реестр и поиск =====

    getKadryRegistry: b.query<KadryRegistry, string>({
      query: (requestId) => `${BASE}/requests/${requestId}/registry`,
      providesTags: (_r, _e, id) => [{ type: 'KadryRegistry', id }],
    }),
    /** Все ответившие «Подходит» по заявкам пользователя (страница «Подходит»). */
    getKadryInterested: b.query<KadryInterestedRequest[], void>({
      query: () => `${BASE}/interested`,
      providesTags: (r) => [
        { type: 'KadryRegistry', id: 'INTERESTED' },
        ...(r ?? []).map((g) => ({ type: 'KadryRegistry' as const, id: g.request_id })),
      ],
    }),
    listKadrySearchRuns: b.query<KadrySearchRun[], string>({
      query: (requestId) => ({ url: `${BASE}/requests/${requestId}/search/runs`, params: { limit: 5 } }),
      providesTags: (_r, _e, id) => [{ type: 'KadrySearchRun', id }],
    }),
    /** Расход запросов API hh поиском: максимум за прогон и остаток пакета. */
    getKadrySearchCost: b.query<KadrySearchCost, void>({
      query: () => `${BASE}/search/cost`,
      providesTags: ['KadrySource'],
    }),
    /** «Искать сейчас»: проверки сразу, поиск и анализ — в фоне. */
    runKadrySearch: b.mutation<KadrySearchRun, string>({
      query: (requestId) => ({ url: `${BASE}/requests/${requestId}/search/run`, method: 'POST' }),
      invalidatesTags: (r, _e, id) =>
        r ? [{ type: 'KadrySearchRun', id }, 'KadryAudit', 'KadryOverview'] : [],
    }),

    // ===== Обзор =====

    /** Сводка дашборда «Обзор» в области заявок пользователя (без ПДн). */
    getKadryOverview: b.query<KadryOverview, void>({
      query: () => `${BASE}/overview`,
      providesTags: ['KadryOverview'],
    }),

    // ===== Предложения =====

    /** «Отправить предложение»: hh-приглашение + сообщения ботов. */
    sendKadryOffer: b.mutation<KadryOutreach, { matchId: string; requestId: string }>({
      query: ({ matchId }) => ({ url: `${BASE}/matches/${matchId}/offer`, method: 'POST' }),
      invalidatesTags: (r, _e, { requestId }) =>
        r ? [{ type: 'KadryRegistry', id: requestId }, 'KadryAudit', 'KadryOverview'] : [],
    }),
    /** Кадровик связался с кандидатом. */
    markKadryContacted: b.mutation<void, { outreachId: string; requestId: string }>({
      query: ({ outreachId }) => ({
        url: `${BASE}/outreach/${outreachId}/contacted`,
        method: 'POST',
      }),
      invalidatesTags: (_r, e, { requestId }) =>
        e ? [] : [{ type: 'KadryRegistry', id: requestId }, 'KadryAudit', 'KadryOverview'],
    }),
    /** Комментарий кадровика к кандидату (переписка у «Связались»). */
    addKadryOutreachComment: b.mutation<
      KadryOutreachComment,
      { outreachId: string; requestId: string; text: string }
    >({
      query: ({ outreachId, text }) => ({
        url: `${BASE}/outreach/${outreachId}/comments`,
        method: 'POST',
        body: { text },
      }),
      invalidatesTags: (_r, e, { requestId }) =>
        e ? [] : [{ type: 'KadryRegistry', id: requestId }, 'KadryAudit'],
    }),

    // ===== Вакансии площадок заявки =====

    listKadryVacancyLinks: b.query<KadryVacancyLink[], string>({
      query: (requestId) => `${BASE}/requests/${requestId}/vacancy-links`,
      providesTags: (_r, _e, id) => [{ type: 'KadryVacancy', id }],
    }),
    setKadryHhVacancy: b.mutation<KadryVacancyLink, { requestId: string; vacancy: string }>({
      query: ({ requestId, vacancy }) => ({
        url: `${BASE}/requests/${requestId}/vacancy-links/hh`,
        method: 'PUT',
        body: { vacancy },
      }),
      invalidatesTags: (r, _e, { requestId }) =>
        r ? [{ type: 'KadryVacancy', id: requestId }, 'KadryAudit'] : [],
    }),
    removeKadryHhVacancy: b.mutation<void, string>({
      query: (requestId) => ({
        url: `${BASE}/requests/${requestId}/vacancy-links/hh`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, e, id) => (e ? [] : [{ type: 'KadryVacancy', id }, 'KadryAudit']),
    }),
    /** Вакансия площадки (hh.ru, Работа.ру): на неё уходят приглашения. */
    setKadryVacancy: b.mutation<
      KadryVacancyLink,
      { requestId: string; source: 'hh' | 'rabota'; vacancy: string }
    >({
      query: ({ requestId, source, vacancy }) => ({
        url: `${BASE}/requests/${requestId}/vacancy-links/${source}`,
        method: 'PUT',
        body: { vacancy },
      }),
      invalidatesTags: (r, _e, { requestId }) =>
        r ? [{ type: 'KadryVacancy', id: requestId }, 'KadryAudit'] : [],
    }),

    // ===== Площадки (OAuth hh) =====

    listKadrySources: b.query<KadrySourceAccount[], void>({
      query: () => `${BASE}/sources`,
      providesTags: ['KadrySource'],
    }),
    connectKadrySource: b.mutation<KadryConnectStart, string>({
      query: (source) => ({ url: `${BASE}/sources/${source}/connect`, method: 'POST' }),
    }),
    discoverKadrySource: b.mutation<void, string>({
      query: (source) => ({ url: `${BASE}/sources/${source}/discover`, method: 'POST' }),
      invalidatesTags: (_r, e) => (e ? [] : ['KadrySource']),
    }),
    disconnectKadrySource: b.mutation<void, string>({
      query: (source) => ({ url: `${BASE}/sources/${source}`, method: 'DELETE' }),
      invalidatesTags: (_r, e) => (e ? [] : ['KadrySource', 'KadryAudit']),
    }),

    // ===== Секреты (ключи приложения hh) =====

    listKadrySecrets: b.query<KadrySecretMeta[], void>({
      query: () => `${BASE}/secrets`,
      providesTags: ['KadrySource'],
    }),
    setKadrySecret: b.mutation<KadrySecretMeta, { name: string; kind: KadrySecretKind; value: string }>({
      query: ({ name, kind, value }) => ({
        url: `${BASE}/secrets/${encodeURIComponent(name)}`,
        method: 'PUT',
        body: { kind, value },
      }),
      invalidatesTags: (r) => (r ? ['KadrySource', 'KadryAudit'] : []),
    }),

    // ===== Боты =====

    listKadryBots: b.query<KadryBotStatus[], void>({
      query: () => `${BASE}/bots`,
      providesTags: ['KadryBot'],
    }),
    /** Канал SMS: ключ, имя отправителя, баланс SMS.ru, отправлено за сутки. */
    getKadrySmsStatus: b.query<KadrySmsStatus, void>({
      query: () => `${BASE}/sms/status`,
      providesTags: ['KadryBot'],
    }),
    setKadryBotToken: b.mutation<KadryBotStatus, { channel: KadryBotChannel; token: string }>({
      query: ({ channel, token }) => ({
        url: `${BASE}/bots/${channel}/token`,
        method: 'PUT',
        body: { token },
      }),
      invalidatesTags: (_r, e) => (e ? ['KadryBot'] : ['KadryBot', 'KadryAudit']),
    }),
    removeKadryBotToken: b.mutation<void, KadryBotChannel>({
      query: (channel) => ({ url: `${BASE}/bots/${channel}/token`, method: 'DELETE' }),
      invalidatesTags: (_r, e) => (e ? [] : ['KadryBot', 'KadryAudit']),
    }),

    // ===== Профили обработки (§1.2) =====

    listKadryProcessingProfiles: b.query<KadryProcessingProfile[], void>({
      query: () => `${BASE}/processing-profiles`,
      providesTags: ['KadryProcessingProfile'],
    }),
    updateKadryProcessingProfile: b.mutation<
      KadryProcessingProfile,
      { id: string; version: number; body: KadryProcessingProfileInput }
    >({
      query: ({ id, version, body }) => ({
        url: `${BASE}/processing-profiles/${id}`,
        method: 'PUT',
        body,
        headers: ifMatch(version),
      }),
      invalidatesTags: (r) => (r ? ['KadryProcessingProfile', 'KadryAudit'] : []),
    }),
    setKadryProcessingProfileStatus: b.mutation<
      KadryProcessingProfile,
      { id: string; version: number; status: 'approved' | 'revoked' }
    >({
      query: ({ id, version, status }) => ({
        url: `${BASE}/processing-profiles/${id}/status`,
        method: 'POST',
        body: { status },
        headers: ifMatch(version),
      }),
      invalidatesTags: (r) => (r ? ['KadryProcessingProfile', 'KadryAudit'] : []),
    }),

    // ===== Деньги (§4.4) =====

    listKadryTariffs: b.query<KadryTariff[], void>({
      query: () => `${BASE}/tariffs`,
      providesTags: ['KadryMoney'],
    }),
    addKadryTariff: b.mutation<KadryTariff, KadryTariff>({
      query: (body) => ({ url: `${BASE}/tariffs`, method: 'POST', body }),
      invalidatesTags: (r) => (r ? ['KadryMoney', 'KadryAudit'] : []),
    }),
    listKadryBudgets: b.query<KadryBudget[], void>({
      query: () => `${BASE}/budgets`,
      providesTags: ['KadryMoney'],
    }),
    createKadryBudget: b.mutation<
      KadryBudget,
      { scope: KadryBudget['scope']; scope_ref: string; limit_rub: number }
    >({
      query: (body) => ({ url: `${BASE}/budgets`, method: 'POST', body }),
      invalidatesTags: (r) => (r ? ['KadryMoney', 'KadryAudit'] : []),
    }),
    setKadryBudgetLimit: b.mutation<KadryBudget, { id: string; version: number; limit_rub: number }>({
      query: ({ id, version, limit_rub }) => ({
        url: `${BASE}/budgets/${id}`,
        method: 'PUT',
        body: { limit_rub },
        headers: ifMatch(version),
      }),
      invalidatesTags: (r) => (r ? ['KadryMoney', 'KadryAudit'] : []),
    }),

    // ===== Уведомления =====

    listKadryNotifications: b.query<KadryNotification[], { unread?: boolean } | void>({
      query: (params) => ({ url: `${BASE}/notifications`, params: params ?? undefined }),
      providesTags: ['KadryNotification'],
    }),
    readKadryNotification: b.mutation<void, string>({
      query: (id) => ({ url: `${BASE}/notifications/${id}/read`, method: 'POST' }),
      invalidatesTags: (_r, e) => (e ? [] : ['KadryNotification']),
    }),
  }),
});

export const {
  useGetKadryRegistryQuery,
  useListKadrySearchRunsQuery,
  useRunKadrySearchMutation,
  useGetKadryInterestedQuery,
  useGetKadrySearchCostQuery,
  useSendKadryOfferMutation,
  useMarkKadryContactedMutation,
  useAddKadryOutreachCommentMutation,
  useListKadryVacancyLinksQuery,
  useSetKadryHhVacancyMutation,
  useRemoveKadryHhVacancyMutation,
  useSetKadryVacancyMutation,
  useListKadrySourcesQuery,
  useConnectKadrySourceMutation,
  useDiscoverKadrySourceMutation,
  useDisconnectKadrySourceMutation,
  useListKadrySecretsQuery,
  useSetKadrySecretMutation,
  useListKadryBotsQuery,
  useGetKadrySmsStatusQuery,
  useSetKadryBotTokenMutation,
  useRemoveKadryBotTokenMutation,
  useListKadryProcessingProfilesQuery,
  useUpdateKadryProcessingProfileMutation,
  useSetKadryProcessingProfileStatusMutation,
  useListKadryTariffsQuery,
  useAddKadryTariffMutation,
  useListKadryBudgetsQuery,
  useCreateKadryBudgetMutation,
  useSetKadryBudgetLimitMutation,
  useListKadryNotificationsQuery,
  useReadKadryNotificationMutation,
  useGetKadryOverviewQuery,
} = kadryOutreachApi;
